import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, debounceTime, defer, filter, map, startWith, tap } from 'rxjs';

const LIST_MAX_HEIGHT = 180;
const LIST_MAX_HEIGHT_WITH_SEARCH_AND_NONE = 135;

/**
 * A reusable, generic <mat-select> wrapper for handling asynchronous data.
 *
 * This component simplifies async data loading by managing loading spinners,
 * error messages, and a retry action directly within the select.
 * It is fully compatible with Angular's Reactive Forms.
 *
 * @example
 * <!-- 1. Simple display with a function -->
 * <rms-async-select
 *     [formControl]="control"
 *     [options]="items"
 *     [loading]="loading"
 *     [error]="error"
 *     [displayWith]="customDisplayWithFn"
 *     (retry)="retry()" />
 *
 * @example
 * <!-- 2. Custom option layouts via ng-template -->
 * <rms-async-select
 *    [formControl]="control"
 *    [options]="items"
 *    [loading]="loading"
 *    [error]="error"
 *    (retry)="retry()">
 *      <ng-template #optionTemplate let-option>
 *        <!-- Custom HTML for each option -->
 *        <mat-icon [svgIcon]="option.icon" />
 *        <span>{{ option.label }}</span>
 *      </ng-template>
 * </rms-async-select>
 * @example
 * <!-- 3. Searchable select -->
 * <rms-async-select
 *    [formControl]="control"
 *    [options]="items"
 *    [searchable]="true"
 *    [searchFn]="countriesSearchFn" // search fn required in case searchable = true
 *    [loading]="loading"
 *    [error]="error"
 *    (retry)="retry()">
 *      <ng-template #optionTemplate let-option>
 *        <!-- Custom HTML for each option -->
 *        <mat-icon [svgIcon]="option.icon" />
 *        <span>{{ option.label }}</span>
 *      </ng-template>
 * </rms-async-select>
 */
@Component({
  selector: 'rms-async-select',
  imports: [
    FormsModule,
    MatFormField,
    MatHint,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatSelectTrigger,
    TranslateModule,
    NgTemplateOutlet,
    NgClass,
    MatInput,
    AsyncPipe,
    CdkFixedSizeVirtualScroll,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
  ],
  templateUrl: './async-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AsyncSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsyncSelectComponent<T> implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public set options(options: T[] | null) {
    this.source$.next(options ?? []);
  }

  @Input() public bindValue: keyof T | null = null;

  @Input() public optionDisabled: ((option: T) => boolean) | null = null;

  @Input() public set error(error: string | null) {
    this.setDisabledState(!!error);

    this.error$.next(error);
  }

  @Input() public set loading(loading: boolean) {
    this.setDisabledState(loading);

    this.loading$.next(loading);
  }

  @Input() public placeholder = '';

  @Input() public noneLabel: string | null = null;

  @Input() public searchable = false;

  @Input() public displayWith: ((value: T) => string) | null = null;
  @Input() public searchFn: ((search: string, options: T[]) => T[]) | null = null;

  @Output() public retry = new EventEmitter<void>();

  @ContentChild('optionTemplate')
  protected optionTemplateRef?: TemplateRef<{ $implicit: T }>;

  @ViewChild(CdkVirtualScrollViewport, { static: true })
  protected cdkVirtualScrollViewPort?: CdkVirtualScrollViewport;

  private readonly destroy$ = inject(DestroyRef);

  protected searchControl = new FormControl<string | null>(null);
  protected control = new FormControl<T | null>(null);
  protected Math = Math;
  protected source$ = new BehaviorSubject<T[]>([]);
  protected loading$ = new BehaviorSubject<boolean>(false);
  protected error$ = new BehaviorSubject<string | null>(null);
  protected filteredOptions$ = combineLatest([
    this.source$,
    this.searchControl.valueChanges.pipe(startWith(null), debounceTime(300)),
  ]).pipe(
    map(([source, search]) => (this.searchFn && search && search.length > 0 ? this.searchFn(search, source) : source))
  );

  protected currentValue$ = combineLatest([
    this.source$,
    defer(() => this.control.valueChanges.pipe(startWith(this.control.value), filter(Boolean))),
  ]).pipe(
    map(([source, current]) =>
      this.bindValue ? source.find((option) => this.bindValue && option[this.bindValue] === current) : current
    )
  );

  private onChange?: (value: T | null) => void;
  private onTouched?: () => void;

  protected get listMaxHeight(): number {
    return this.searchable && this.noneLabel !== null ? LIST_MAX_HEIGHT_WITH_SEARCH_AND_NONE : LIST_MAX_HEIGHT;
  }

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        tap((value) => {
          this.onTouched?.();
          this.onChange?.(value);
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  writeValue(value: T | null): void {
    this.control.patchValue(value, { emitEvent: true });
  }

  public registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.control.enabled && this.control.disable({ emitEvent: false })
      : this.control.disabled && this.control.enable({ emitEvent: false });
  }

  public openChange($event: boolean): void {
    if ($event) {
      this.cdkVirtualScrollViewPort?.scrollToIndex(0);
      this.cdkVirtualScrollViewPort?.checkViewportSize();
    }
  }
}
